import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { TooltipDirective, TooltipOptions } from 'ng2-tooltip-directive';
import { FiTooltipContentType, FiTooltipMode } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { Subject } from 'rxjs';
import { FiTooltipService } from '@fi-shared/fi-tooltip/fi-tooltip.service';
import { AppTextService } from '@fi-shared/app-text';
import { TTooltipTextAlign } from '@fi-shared/info-icon/info-icon.component';

type T_FiTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

@Directive()
export abstract class FiTooltipBase implements OnInit, AfterViewInit, OnDestroy {
    @Input() contentElement: HTMLElement; // parent by default
    @Input() contentType: FiTooltipContentType = FiTooltipContentType.PlainText;
    @Input() contentText: string; // used for FiTooltipContentType.PlainText type
    @Input() contentIconClass: string; // used for FiTooltipContentType.PlainText type

    @Input() insertFileErrorContentText: string; // used for FiTooltipContentType.InsertFile type

    @Input() tooltipMode = FiTooltipMode.Absolute;
    @Input() placement: T_FiTooltipPlacement = 'right';
    @Input() tooltipTextAlignment: TTooltipTextAlign = 'left';
    @Input() tooltipClass: string;
    @Input() set scrollableElementRefs(elements: ElementRef<HTMLElement>[]) {
        if (!!elements?.length)
            elements
                .filter((el) => el)
                .forEach((scrollableElement: ElementRef<HTMLElement>) => {
                    scrollableElement.nativeElement.addEventListener('scroll', () => {
                        this.fiTooltipService.updateTooltipPositionOnScroll(() => this.tooltipRef);
                    });
                });
    }

    @ViewChild(TooltipDirective, { static: false }) tooltipRef: TooltipDirective;

    constructor(protected injector: Injector) {}

    ngOnInit(): void {
        this.setServices();
    }

    ngAfterViewInit() {
        if (this.tooltipMode === FiTooltipMode.Absolute) {
            const el: HTMLElement = this.elRef.nativeElement;
            this.renderer.setStyle(el, 'position', 'absolute');
            this.renderer.setStyle(el.parentElement, 'position', 'relative');
            if (this.placement === 'right') {
                this.renderer.setStyle(el, 'top', '50%');
                this.renderer.setStyle(el, 'right', '0px');
            } else if (this.placement === 'top') {
                this.renderer.setStyle(el, 'top', '0px');
                this.renderer.setStyle(el, 'left', '50%');
            } else {
                throw new Error(`Not implemented absolute tooltip style for placement ${this.placement}`);
            }
        }
    }

    protected getContentElement(): HTMLElement {
        return this.contentElement ?? this.elRef.nativeElement.parentElement;
    }

    protected getPlainContentText() {
        return this.contentText ?? this.appTextService.get('common_error.mandatory');
    }

    protected getInsertFileContentText() {
        return this.insertFileErrorContentText ?? this.appTextService.get('ccmFileUpload.only_videos_images');
    }

    protected getTooltipOptions(): TooltipOptions {
        return <TooltipOptions>{
            'animation-duration': 200,
            'hide-delay-mobile': 0,
            'hide-delay': 0,
            trigger: 'none',
            contentType: 'template',
            theme: 'light',
            placement: this.placement,
            'tooltip-class': `fi-tooltip-content fi-rounded-white-tooltip rounded-arrow ${this.getTooltipContentClasses().join(' ')}`,
        };
    }

    protected getTooltipContentClasses(): string[] {
        const textAlignClass: string = 'text-align-' + this.tooltipTextAlignment;
        const classes: string[] = ['fi-tooltip-content', 'fi-rounded-white-tooltip', 'rounded-arrow', textAlignClass];
        if (this.tooltipClass) {
            classes.push(this.tooltipClass);
        }
        return classes;
    }

    showTooltip() {
        this.setTooltipVisibility(true);
    }

    hideTooltip() {
        this.setTooltipVisibility(false);
    }

    protected setTooltipVisibility(visible: boolean): void {
        if (visible) {
            this.tooltipRef?.show();
        } else {
            this.tooltipRef?.hide();
        }
    }

    updateTooltipPosition() {
        this.fiTooltipService.updateTooltipPosition(this.tooltipRef);
    }

    protected fiTooltipService: FiTooltipService;
    protected elRef: ElementRef<HTMLElement>;
    protected renderer: Renderer2;
    protected cdr: ChangeDetectorRef;
    protected appTextService: AppTextService;
    protected setServices() {
        this.fiTooltipService = this.injector.get(FiTooltipService);
        this.elRef = this.injector.get(ElementRef<HTMLElement>);
        this.renderer = this.injector.get(Renderer2);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.appTextService = this.injector.get(AppTextService);
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.hideTooltip();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
